import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './client';
import type { Page } from './types';
import { useSession } from '@/stores/session';

const EMPTY_OPTIONS: never[] = [];

type Query = Record<string, string | number | boolean | null | undefined>;

export async function getAllOptions<T>(path: string, query: Query = {}, signal?: AbortSignal): Promise<T[]> {
  const url = new URL(path, 'http://v2.local');
  const params = { ...Object.fromEntries(url.searchParams), ...query };
  const items: T[] = [];
  let page = 1;
  do {
    const result = await api.get<Page<T>>(url.pathname, { ...params, page, pageSize: 500 }, signal);
    items.push(...result.items);
    if (!result.pagination.hasNext) return items;
    page += 1;
  } while (!signal?.aborted);
  throw new DOMException('请求已取消', 'AbortError');
}

// Refresh on reopening a form, returning from another tab, or explicit refresh.
// No permanent promise cache, and no options shared across user sessions.
export function useCatalog<T>(path: string | null) {
  const access = useSession((s) => JSON.stringify([s.user?.id, s.user?.warehouseIds, s.user?.permissions]));
  const identity = JSON.stringify([path, access]);
  const [state, setState] = useState<{ identity: string; path: string | null; data: T[]; loading: boolean; error?: Error }>({ identity, path, data: [], loading: !!path });
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((n) => n + 1), []);
  useEffect(() => {
    window.addEventListener('ims:v2:refresh', reload); window.addEventListener('focus', reload);
    return () => { window.removeEventListener('ims:v2:refresh', reload); window.removeEventListener('focus', reload); };
  }, [reload]);
  useEffect(() => {
    if (!path) return;
    const controller = new AbortController();
    setState((current) => ({ identity, path, data: current.identity === identity ? current.data : [], loading: true }));
    void getAllOptions<T>(path, {}, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setState({ identity, path, data, loading: false }); })
      .catch((error: Error) => { if (!controller.signal.aborted) setState((current) => ({ ...current, loading: false, error })); });
    return () => controller.abort();
  }, [path, revision, identity]);
  return { ...(path && state.identity === identity ? state : { path, data: EMPTY_OPTIONS as T[], loading: !!path, error: undefined as Error | undefined }), reload };
}

export function useOptionSearch<T>(path: string | null, query: Query) {
  const access = useSession((s) => JSON.stringify([s.user?.id, s.user?.warehouseIds, s.user?.permissions]));
  const params = JSON.stringify(query);
  const key = JSON.stringify([path, params, access]);
  const [state, setState] = useState<{ key: string; data: T[]; page: number; hasNext: boolean; loading: boolean; error?: Error }>({ key, data: [], page: 0, hasNext: false, loading: !!path });
  const controller = useRef<AbortController | null>(null);
  const load = useCallback(async (page: number) => {
    if (!path) return;
    controller.current?.abort();
    const current = new AbortController(); controller.current = current;
    setState((s) => ({ ...s, key, data: page === 1 ? [] : s.data, loading: true, error: undefined }));
    try {
      const result = await api.get<Page<T>>(path, { ...JSON.parse(params), page, pageSize: 100 }, current.signal);
      if (!current.signal.aborted) setState((s) => ({ key, data: page === 1 ? result.items : [...s.data, ...result.items], page, hasNext: result.pagination.hasNext, loading: false }));
    } catch (error) { if (!current.signal.aborted) setState((s) => ({ ...s, loading: false, error: error as Error })); }
  }, [path, params, key]);
  useEffect(() => { void load(1); return () => controller.current?.abort(); }, [load]);
  const visible = path && state.key === key ? state : { key, data: EMPTY_OPTIONS as T[], page: 0, hasNext: false, loading: !!path };
  return { ...visible, loadMore: () => { if (!visible.loading && visible.hasNext) void load(visible.page + 1); }, reload: () => void load(1) };
}
